import { CommonModule } from '@angular/common';
import { Component, DestroyRef, effect, inject, Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from "@angular/material/icon";
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatToolbar } from '@angular/material/toolbar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { finalize, first, Subscription } from 'rxjs';
import { Tools } from 'src/app/classes/shared/tools';
import { DeleteModalComponent } from 'src/app/components/shared/dialogs/delete-modal/delete-modal.component';
import { EditModalComponent } from 'src/app/components/shared/dialogs/edit-modal/edit-modal.component';
import { Model } from 'src/app/interfaces/api/model';
import { Project } from 'src/app/interfaces/api/project';
import { ModelService } from 'src/app/services/api/models/model.service';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { ButtonComponent, FileInputComponent } from 'ui-library';

const NO_MODEL_THUMBNAIL = '/img/no_model.png';

@Component({
    selector: 'app-models',
    imports: [
        CommonModule,
        ButtonComponent,
        FileInputComponent,
        TranslateModule,
        MatCardModule,
        MatProgressBar,
        MatToolbar,
        MatIconModule,
        ReactiveFormsModule
    ],
    standalone: true,
    templateUrl: './models.component.html',
    styleUrl: './models.component.scss'
})

export class ModelsComponent {
    // Services 
    private readonly tools: Tools = inject(Tools);
    private readonly modelService: ModelService = inject(ModelService);
    public readonly dialog: MatDialog = inject(MatDialog);
    private readonly projectService: ProjectService = inject(ProjectService);
    private readonly destroyRef: DestroyRef = inject(DestroyRef);
    private readonly translate: TranslateService = inject(TranslateService);

    // Variables
    uploadProgress: number = 0;
    uploadInProgress: boolean = false;
    uploadError: any = null;
    accept: string = '*/*';
    private formats: string[] | null = null;
    models: Model[] = [];
    private modelsSubscription?: Subscription;
    private readonly thumbnails = new Map<string, string>();
    private readonly pendingThumbnails = new Set<string>();

    // Signals
    readonly currentProject: Signal<Project> = this.projectService.currentProjectSignal;

    constructor() {
        effect(() => {
            const project = this.currentProject();
            if (project?.id) {
                this.getListModels();
            }
        });

        this.modelService.getFormats().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(formats => {
            this.formats = formats;
            this.accept = formats?.length ? formats.map(ext => '.' + ext).join(',') : '*/*';
        });
    }

    deleteModel(model: Model) {
        const dialog = this.dialog.open(DeleteModalComponent, {
            width: '400px',
            data: { title: 'Model', name: model.name }
        })

        dialog.afterClosed().subscribe(d => {
            if (d) this.modelService.removeModel(model.id).pipe(first()).subscribe(() => this.getListModels(), err => console.error(err))
            else return;
        })
    }

    editModel(model: Model) {
        const edit = this.dialog.open(EditModalComponent, {
          width: '600px',
          data: { name: "form.name", value: model.name }
        })

        edit.afterClosed().subscribe((result) => {
            if (result?.updated) {
                this.modelService.updateModel({ id: model.id, name: result.value }).subscribe(res =>  this.getListModels(), err => console.error(err))
            }
        })
    }

    loadModel(event: any) {
        if (event[0]) {
            const file = event[0];

            this.uploadError = null;
            if (!this.isSupportedFile(file.name)) {
                this.uploadError = this.translate.instant('settings.project.model_unsupported_format', { name: file.name });
                return;
            }

            this.uploadInProgress = true;
            this.uploadProgress = 0;
            this.modelService.uploadModel(file, { type: '3d' }).subscribe((res: any) => {

                if (res.loaded) {

                    this.uploadProgress = (res.loaded / res.total) * 100;
                    if (this.uploadProgress == 100) {
                        
                        this.uploadInProgress = false;
                    }
                }

            }, (err: any) => {
                console.error(err);
                this.uploadError = this.uploadErrorMessage(err, file.name);
                this.uploadInProgress = false;
            })
        }
    }

    private isSupportedFile(name: string): boolean {
        if (!this.formats) return true;
        const dot = name.lastIndexOf('.');
        return dot > 0 && this.formats.includes(name.slice(dot + 1).toLowerCase());
    }

    private uploadErrorMessage(err: any, name: string): string {
        if (err?.error?.error === 'unsupported_format')
            return this.translate.instant('settings.project.model_unsupported_format', { name });
        if (typeof err?.error?.message === 'string' && err.error.message)
            return err.error.message;
        return err?.message;
    }

    isTranslating(model: Model): boolean {
        return model.status === 'uploading' || model.status === 'translating';
    }

    getListModels() {
        // getModels() ne se termine jamais : on remplace l'abonnement précédent au lieu d'en empiler un nouveau
        this.modelsSubscription?.unsubscribe();
        this.modelsSubscription = this.modelService.getModels().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((models: Model[]) => {
            this.models = models;
            this.models.forEach((model: Model) => this.applyThumbnail(model));
        })
    }

    private applyThumbnail(model: Model) {
        const key = `${model.id}:${model.urn}`;
        model.thumbnail = this.thumbnails.get(key) || NO_MODEL_THUMBNAIL;

        if (model.status !== 'processed' || !model.urn || this.thumbnails.has(key) || this.pendingThumbnails.has(key)) return;

        this.pendingThumbnails.add(key);
        this.modelService.getThumbnail(model.urn.toString()).pipe(
            first(),
            finalize(() => this.pendingThumbnails.delete(key)),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe({
            next: (res: any) => {
                if (!res?.data) return;
                this.thumbnails.set(key, res.data);
                this.models.filter(m => `${m.id}:${m.urn}` === key).forEach(m => m.thumbnail = res.data);
            },
            error: (err: any) => console.error(err)
        })
    }
}
